// The town (phase D1's town stream; since phase D joined, part of the default scene, `?scene=town` naming the same
// world): the streamed valley (src/world/) with the townsfolk and the cutscenes added to its one kernel game. The
// world's variant is wrapped, not changed: its `extend` runs first (the fight, the things, the sky, the rooms, the rpg
// layer, discovery, the story), then the town's modules join after it, the NPCs in the things' interaction registry.
// The story asks for the scenes (`story:scene`) and opens its dialogue on `npc:interact`.
import type { Placed } from "@rpg/camera";
import type { Runtime } from "@rpg/runtime";
import type { MoveDrive } from "@rpg/controller";
import type { SpikeContent } from "../content";
import type { WorldMount } from "../world";
import { readTownContent, type TownContent } from "./content";
import { createCutscenes, type Cutscenes } from "./cutscene";
import { TownFacts } from "./facts";
import { createTownsfolk, type Townsfolk } from "./module";
import { townPlaces, type TownPlaces } from "./places";

/** The town's parts, once the scene is set up. */
export interface TownMount {
  content: TownContent;
  places: TownPlaces;
  facts: TownFacts;
  folk(): Townsfolk;
  cutscenes(): Cutscenes;
  world: WorldMount;
}

/** Wrap the world mount's variant with the town. Its facts (`time.hours`, `npc.<id>.place`) go on the world's one
 *  condition language (`m.conditions`, @rpg/story's). */
export function townScene(m: WorldMount, o: { content: SpikeContent; runtime: Runtime; params: URLSearchParams }): TownMount {
  const content = readTownContent(o.content);
  const places = townPlaces(m, content);
  const facts = new TownFacts(m.conditions);
  const t = o.content.t;
  let folk: Townsfolk | null = null;
  let cuts: Cutscenes | null = null;
  const base = m.variant;
  m.variant = {
    ...base,
    // the world's own name (phase D joined: the town is part of the default scene, so its saves are the world's)
    name: base.name,
    extend(args) {
      const ext = base.extend(args);
      const rig = args.rig;
      const f = createTownsfolk({
        content,
        places,
        nav: m.nav(),
        hours: () => m.sky().hours(),
        hero: () => {
          const h = rig.hero();
          return { x: h.feet.x, y: h.feet.y, z: h.feet.z, vx: h.vel.x, vz: h.vel.z };
        },
        // the live enemies near the hero (the townsfolk are only on the crowd near the hero)
        others: () => {
          const h = rig.hero();
          return m
            .fight()
            .state()
            .enemies.filter((e) => e.alive && Math.hypot(e.pos[0] - h.feet.x, e.pos[2] - h.feet.z) < 60)
            .map((e) => ({ x: e.pos[0], y: e.pos[1], z: e.pos[2] }));
        },
        groundAt: (x, z) => m.world.heightAt(x, z),
        interaction: () => m.things().interaction,
        facts,
      });
      folk = f;
      const puppetMarks = new Map<string, string>();
      for (const s of content.scenes.values()) for (const p of s.puppets) puppetMarks.set(p.actor, p.mark);
      const c = createCutscenes({
        content,
        actor: (id): Placed | null => {
          if (id === "hero") {
            const h = rig.hero();
            return { pos: [h.feet.x, h.feet.y, h.feet.z], yaw: h.yaw };
          }
          const pm = puppetMarks.get(id);
          if (pm) return places.mark(pm);
          if (!f.state().npcs.some((n) => n.id === id)) return null;
          const n = f.npc(id);
          return { pos: [...n.pos], yaw: n.yaw };
        },
        mark: (id) => places.mark(id),
        putDown: (id, at) => {
          if (id === "hero") rig.warpHero(at.pos[0], at.pos[1], at.pos[2], at.yaw);
          else f.place(id, at.pos, at.yaw);
        },
        onEvent: (name) => {
          // the Warden wakes: alert to the hero, wherever it was standing
          if (name !== "warden:awake") return;
          const st = m.fight().state();
          const h = rig.hero();
          for (const e of st.enemies) {
            if (!e.alive || e.kind !== "warden") continue;
            e.percept = { level: "alert", awareness: 1, pending: null, target: st.heroId, lastKnown: [h.feet.x, h.feet.y, h.feet.z], sees: false, unseen: 0, cause: "noise" };
          }
        },
        text: (id) => t(id),
      });
      cuts = c;
      return {
        ...ext,
        modules: [...ext.modules, f.module, c.module],
        build: (w) => {
          ext.build(w);
          f.build(w);
          c.build(w);
          // the NPCs share phase C's interaction registry: a press on one comes back here
          m.things().other = (id) => {
            if (f.owns(id)) f.interact(id);
          };
        },
        hashed: [...ext.hashed, ...f.hashed, ...c.hashed],
        // the hero stands still while a scene plays (the world is paused anyway) or while it talks
        heroDrive: (): MoveDrive | null => ext.heroDrive(),
      };
    },
  };
  const need = <T>(v: T | null, what: string): T => {
    if (!v) throw new Error(`town: ${what} not set up yet`);
    return v;
  };
  return { content, places, facts, world: m, folk: () => need(folk, "the townsfolk"), cutscenes: () => need(cuts, "the cutscenes") };
}
